import React from 'react';
import moment from 'moment';
import { Tooltip } from '_atoms';
import type { TableColumnType } from '_atoms/Table/types';
import { ROW_NAMES, type SscMonthKey, type SscTotalKey } from './constants';

export interface SscMonthAnalytics {
  year: number;
  month: number;
  month_created_by_system: number;
  month_deleted_by_system: number;
  month_marked_as_reviewed: number;
  month_assigned_to_job: number;
  month_blacklisted: number;
  month_not_reviewed: number;
  month_manually_created_candidates: number;
  month_left_from_previous_period: number;
  [key: string]: number;
}

export interface SscTotalAnalytics {
  total_created_by_system: number;
  total_deleted_by_system: number;
  total_marked_as_reviewed: number;
  total_assigned_to_job: number;
  total_blacklisted: number;
  total_not_reviewed: number;
  total_manually_created_candidates: number;
  total_left_from_previous_period: number;
  [key: string]: number;
}

export interface SscStatisticsResponse {
  analytics_by_month: SscMonthAnalytics[];
  analytics_total: SscTotalAnalytics;
}

export interface SscRatioCell {
  value: number | string;
  tooltip: string;
  percentage: string;
}

export type SscCellValue = number | string | SscRatioCell;

export interface SscTableRow {
  action: string;
  total: SscCellValue;
  id?: string;
  uuid?: string;
  [key: string]: unknown;
}

export const dataMapper = ({ analytics_total, analytics_by_month }: SscStatisticsResponse): SscTableRow[] =>
  ROW_NAMES.map(({ name, key, totalKey }, idx) => ({
    action: name,
    total: ![0, 1, 7].includes(idx)
      ? {
          value: analytics_total[totalKey as SscTotalKey] || '-',
          tooltip: analytics_total[totalKey as SscTotalKey]
            ? `Total ${name} / Total ${ROW_NAMES[0].name} = (${analytics_total[totalKey as SscTotalKey]}/${
                analytics_total.total_created_by_system
              })*100%`
            : '-',
          percentage: analytics_total[totalKey as SscTotalKey]
            ? ` (${
                analytics_total.total_created_by_system
                  ? Math.round(
                      (analytics_total[totalKey as SscTotalKey] / analytics_total.total_created_by_system) * 1000
                    ) / 10
                  : 0
              }%)`
            : '',
        }
      : analytics_total[totalKey as SscTotalKey],
    ...analytics_by_month.reduce<Record<string, SscCellValue>>((acc, data) => {
      acc[String(data.month + data.year)] =
        ![0, 1, 7].includes(idx) && data[key as SscMonthKey]
          ? {
              value: data[key as SscMonthKey],
              tooltip: `${moment(data.month, 'M').format('MMMM')} ${name} / ${moment(data.month, 'M').format('MMMM')} ${
                ROW_NAMES[0].name
              } = (${data[key as SscMonthKey]}/${data.month_created_by_system})*100%`,
              percentage: ` (${
                data.month_created_by_system
                  ? Math.round((data[key as SscMonthKey] / data.month_created_by_system) * 1000) / 10
                  : 0
              }%)`,
            }
          : data[key as SscMonthKey] || '-';
      return acc;
    }, {}),
  }));

const renderCell = (data: unknown) =>
  typeof data === 'object' && data !== null ? (
    <Tooltip label={(data as SscRatioCell).tooltip}>
      {(data as SscRatioCell).value}
      {(data as SscRatioCell).percentage}
    </Tooltip>
  ) : (
    (data as React.ReactNode)
  );

export const columnsMapper = ({
  analytics_by_month,
}: Pick<SscStatisticsResponse, 'analytics_by_month'>): TableColumnType[] => [
  {
    title: 'Action',
    key: 'action',
    dataIndex: 'action',
    width: 240,
    fixed: 'left',
  },
  {
    title: `Total (${analytics_by_month.length} month)`,
    key: 'total',
    dataIndex: 'total',
    width: 200,
    align: 'right',
    render: data => renderCell(data),
  },
  ...analytics_by_month.map(el => ({
    title: moment(el.month, 'M').format('MMMM'),
    key: String(el.month + el.year),
    dataIndex: String(el.month + el.year),
    width: 200,
    align: 'right' as const,
    render: (data: unknown) => renderCell(data),
  })),
];
